import { useCallback } from 'react';
import useTranslation from 'providers/i18n/hooks/useTranslation';

//values that can be dynamically replaced in message
type Values = Record<string | number, string | number>;

type FormatMessageParams = {
  name?: string;
  id: string;
  defaultMessage?: string;
  values?: Values;
};

//custom hook to be used at top level of components
export const useFormat = (args?: Pick<FormatMessageParams, 'name'>) => {
  //get the translations from the passed file
  const { translate } = useTranslation();

  // Helper function to interpolate variables in a string
  const interpolate = (message: string, values: Record<string, any>) => {
    return message.replace(/{(\w+)}/g, (_, key) => values[key] ?? `{${key}}`);
  };

  // Main function for translating messages
  const formatMessage = useCallback(
    ({ id, defaultMessage, values = {} }: Omit<FormatMessageParams, 'name'>) => {
      // Create the key for translation
      const key = `${args?.name}.${id}`;

      // Try to translate the message using the given key
      let translatedMessage = translate(key, { values });

      // If the key is not found, use the defaultMessage or id
      if (translatedMessage === key) {
        translatedMessage = defaultMessage ?? key;
      }

      // Interpolate variables if defaultMessage is used or if translation does not handle it
      if (translatedMessage === defaultMessage || translatedMessage === key) {
        translatedMessage = interpolate(translatedMessage, values);
      }

      return translatedMessage;
    },
    [translate, args?.name],
  );

  return { formatMessage };
};
